"use client";

import React, { useState, useEffect } from 'react';
import ContactForm from './ContactForm';
import { trackEvent } from '@/lib/analytics';
import styles from './ContactModal.module.css';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  triggerSource?: string; // For analytics tracking
}

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, triggerSource = 'unknown' }) => {
  useEffect(() => {
    if (isOpen) {
      // Track modal open event
      trackEvent('contact_modal_opened', {
        source: triggerSource,
        timestamp: new Date().toISOString()
      });

      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      // Add escape key listener
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      
      document.addEventListener('keydown', handleEscape);
      
      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, onClose, triggerSource]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      // Track modal close event
      trackEvent('contact_modal_closed', {
        source: triggerSource,
        method: 'overlay_click',
        timestamp: new Date().toISOString()
      });
      onClose();
    }
  };

  const handleCloseClick = () => {
    // Track modal close event
    trackEvent('contact_modal_closed', {
      source: triggerSource,
      method: 'close_button',
      timestamp: new Date().toISOString()
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContainer}>
        <button 
          className={styles.closeButton}
          onClick={handleCloseClick}
          aria-label="Close contact form"
        >
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <div className={styles.modalContent}>
          <ContactForm />
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
